import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ManageAnnouncements = () => {
    const navigate = useNavigate();
    const { confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    
    // Form State
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [buttonText, setButtonText] = useState('');
    const [buttonUrl, setButtonUrl] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchAnnouncements();
        // eslint-disable-next-line
    }, []);

    const fetchAnnouncements = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_announcements`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setAnnouncements(data.announcements);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setImageFile(null);
            setImagePreview('');
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setStatusMessage('');
        
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('message', message);
            if (buttonText) formData.append('button_text', buttonText);
            if (buttonUrl) formData.append('button_url', buttonUrl);
            if (imageFile) formData.append('image', imageFile);

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=create_announcement`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            
            if (data.success) {
                setStatusMessage(data.message);
                setStatusType('success');
                setTitle(''); setMessage(''); setButtonText(''); setButtonUrl(''); setImageFile(null); setImagePreview('');
                if(document.getElementById('image')) document.getElementById('image').value = '';
                fetchAnnouncements();
            } else {
                setStatusMessage(data.message);
                setStatusType('error');
            }
        } catch (err) {
            setStatusMessage('Network error occurred.');
            setStatusType('error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleAction = async (id, action) => {
        const confirmMsg = action === 'delete' ? 'Permanently delete this announcement?' : `Are you sure you want to ${action} this announcement?`;
        if (!await customConfirm(confirmMsg, action === 'delete' ? 'Delete Announcement' : 'Confirm Action', action === 'delete' ? 'danger' : 'warning')) return;

        setStatusMessage('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=update_announcement_status`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, status_action: action })
            });
            const data = await res.json();
            if (data.success) {
                setStatusMessage(data.message);
                setStatusType('success');
                fetchAnnouncements();
            } else {
                setStatusMessage(data.message);
                setStatusType('error');
            }
        } catch (err) {
            setStatusMessage('Network error occurred.');
            setStatusType('error');
        }
    };

    return (
        <SuperAdminLayout>
                    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '2rem' }}>
                            <h2 className="sa-page-title">Manage Announcements</h2>
                            <p className="sa-page-subtitle">Publish dashboard messages to all restaurant owners.</p>
                        </div>

                        {statusMessage && (
                            <div style={{
                                padding: '12px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '20px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '10px',
                                color: statusType === 'success' ? 'var(--success)' : 'var(--danger)',
                                background: statusType === 'success' ? 'var(--success-bg)' : 'var(--danger-bg)',
                                border: `1px solid ${statusType === 'success' ? 'var(--success)' : 'var(--danger)'}`
                            }}>
                                <i className={`fas ${statusType === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                                {statusMessage}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            {/* Create Section */}
                            <div style={{ flex: '1 1 450px', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                <div className="sa-panel" style={{ margin: 0 }}>
                                    <h3 className="sa-panel-title"><i className="fas fa-plus-circle"></i> Create New Announcement</h3>
                                    <form onSubmit={handleCreate} className="sa-form-group">
                                        <div style={{ marginBottom: '1.5rem' }}>
                                            <label className="sa-form-label">Title</label>
                                            <input type="text" className="sa-input" style={{ width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g. New Feature Release!" />
                                        </div>

                                        <div style={{ marginBottom: '1.5rem' }}>
                                            <label className="sa-form-label">Message</label>
                                            <textarea className="sa-input" rows="4" style={{ width: '100%', resize: 'vertical' }} value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Describe the announcement..."></textarea>
                                        </div>

                                        <div style={{ marginBottom: '1.5rem' }}>
                                            <label className="sa-form-label">Cover Image (Optional)</label>
                                            <div style={{ border: '2px dashed var(--border-color)', padding: '15px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-dark)', transition: 'all 0.2s' }}>
                                                <input type="file" id="image" accept="image/*" onChange={handleFileChange} style={{ color: 'var(--text-secondary)', display: 'block', width: '100%' }} />
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '15px', marginBottom: '2rem' }}>
                                            <div style={{ flex: 1 }}>
                                                <label className="sa-form-label">Button Text (Optional)</label>
                                                <input type="text" className="sa-input" placeholder="e.g. Learn More" style={{ width: '100%' }} value={buttonText} onChange={(e) => setButtonText(e.target.value)} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label className="sa-form-label">Button Link (Optional)</label>
                                                <input type="text" className="sa-input" placeholder="https://..." style={{ width: '100%' }} value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)} />
                                            </div>
                                        </div>

                                        <button type="submit" className="sa-primary-btn" style={{ width: '100%', padding: '15px', fontSize: '1.1rem' }} disabled={isCreating}>
                                            {isCreating ? <><i className="fas fa-circle-notch fa-spin"></i> Posting...</> : <><i className="fas fa-bullhorn"></i> Post Announcement</>}
                                        </button>
                                    </form>
                                </div>

                                <div className="sa-panel" style={{ margin: 0, background: 'var(--bg-dark)', borderColor: 'var(--border-color)', borderStyle: 'dashed', borderWidth: '2px' }}>
                                    <h4 className="sa-panel-title" style={{ borderBottom: 'none', marginBottom: '5px', padding: 0 }}><i className="fas fa-eye"></i> Live Preview</h4>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>This is how the announcement will appear on the restaurant admin dashboards.</p>
                                    
                                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', padding: '20px', borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                        <h3 style={{ marginTop: 0, color: 'var(--text-primary)', fontSize: '1.2rem' }}>{title || 'Announcement Title'}</h3>
                                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.5 }}>{message || 'The content of your announcement will be displayed here for the users to read.'}</p>
                                        
                                        {imagePreview && (
                                            <div style={{ width: '100%', overflow: 'hidden', borderRadius: '8px', marginTop: '15px', backgroundColor: '#f5f5f5', border: '1px solid #eaeaea' }}>
                                                <img src={imagePreview} alt="Preview" style={{ width: '100%', display: 'block', objectFit: 'contain', maxHeight: '200px' }} />
                                            </div>
                                        )}
                                        
                                        {buttonText && (
                                            <div style={{ display: 'inline-block', marginTop: '20px', padding: '10px 24px', backgroundColor: 'var(--accent-primary)', color: 'white', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: '0.9rem' }}>
                                                {buttonText}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* History Section */}
                            <div className="sa-panel" style={{ flex: '1 1 500px', margin: 0, padding: 0, overflow: 'hidden' }}>
                                <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)' }}>
                                    <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem' }}><i className="fas fa-history"></i> Announcement History</h3>
                                </div>
                                <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                    <table className="sa-table" style={{ margin: 0 }}>
                                        <thead>
                                            <tr>
                                                <th>Title</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                                <th style={{ textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}><i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: 'var(--accent-primary)', marginBottom: '10px' }}></i><br/>Loading announcements...</td></tr>
                                            ) : announcements.length === 0 ? (
                                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}><i className="fas fa-inbox fa-3x" style={{ color: 'var(--border-color)', marginBottom: '1rem' }}></i><p>No announcements found.</p></td></tr>
                                            ) : announcements.map(ann => (
                                                <tr key={ann.id}>
                                                    <td><strong style={{ color: 'var(--text-primary)' }}>{ann.title}</strong></td>
                                                    <td>
                                                        <span style={{ 
                                                            display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                                                            background: Number(ann.is_active) === 1 ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-dark)',
                                                            color: Number(ann.is_active) === 1 ? 'var(--success)' : 'var(--text-secondary)',
                                                            border: `1px solid ${Number(ann.is_active) === 1 ? 'rgba(34, 197, 94, 0.2)' : 'var(--border-color)'}`
                                                        }}>
                                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: Number(ann.is_active) === 1 ? 'var(--success)' : 'var(--text-secondary)' }}></span>
                                                            {Number(ann.is_active) === 1 ? 'Active' : 'Archived'}
                                                        </span>
                                                    </td>
                                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{new Date(ann.created_at).toLocaleDateString()}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                            {Number(ann.is_active) === 1 ? (
                                                                <button onClick={() => handleAction(ann.id, 'archive')} className="sa-secondary-btn" style={{ padding: '6px 12px', fontSize: '0.85rem' }} title="Archive"><i className="fas fa-archive"></i></button>
                                                            ) : (
                                                                <button onClick={() => handleAction(ann.id, 'activate')} className="sa-secondary-btn" style={{ padding: '6px 12px', fontSize: '0.85rem', color: 'var(--success)', borderColor: 'var(--success)' }} title="Activate"><i className="fas fa-check"></i></button>
                                                            )}
                                                            <button onClick={() => handleAction(ann.id, 'delete')} className="sa-secondary-btn" style={{ padding: '6px 12px', fontSize: '0.85rem', color: 'var(--danger)', borderColor: 'var(--danger)' }} title="Delete"><i className="fas fa-trash-alt"></i></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
        </SuperAdminLayout>
    );
};

export default ManageAnnouncements;
